import React, { useCallback, useEffect, useState } from "react";

import {
  FilePdfOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  SendOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Button, Popover } from "antd";
import { format } from "date-fns";
import useInView from "react-cool-inview";

import { CompanyParams } from "../Context/companyContext";
import { usePermissions } from "../Context/permissionContext";
import api from "../services/api";
import { notifyError, notifySucces } from "../utils/notifications";
import { useAuth } from "./AuthProvider";

function isNoActiveInsurancesForHolderError(err) {
  const msg = err?.message || "";
  return (
    msg.includes("Não foram encontrados seguros") && msg.includes("status: 404")
  );
}

function useInsurance(clientId, handleOpenModalSendMailTermInsurance = null) {
  const [insurances, setInsurances] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [detailInsurance, setDetailInsurance] = useState(null);
  const [detailInsuranceModalOpen, setDetailInsuranceModalOpen] =
    useState(false);
  const [loadingPdfBilhet, setLoadingPdfBilhet] = useState(false);
  const [loadingPdfTerm] = useState(false);
  const [handleClickRow, setHandleClickRow] = useState({});
  const [loadingCanceledInsurances, setLoadingCanceledInsurances] =
    useState(false);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [canceledInsurances, setCanceledInsurances] = useState([]);
  const [hasErrorOnInsurancesRequest, setHasErrorOnInsurancesRequest] =
    useState(false);
  const { permissions } = usePermissions();
  const { user } = useAuth();
  const { companyParams } = CompanyParams();
  const hasSecurity = Boolean(companyParams?.has_security);

  const printTicketInsurance = useCallback(async (row) => {
    try {
      setLoadingPdfBilhet(true);
      const resp = await api.get(
        "insurance_ticket_contracts",
        { client_id: clientId, contract_id: row.codigoAdesao },
        { "Content-Type": "application/pdf" },
        true
      );
      const fileURL = URL.createObjectURL(await resp.blob());
      const pdfWindow = window.open(fileURL);
      setTimeout(() => {
        pdfWindow.document.title = "Bilhete_Contrato.pdf";
      }, 750);
      setLoadingPdfBilhet(false);
    } catch (error) {
      setLoadingPdfBilhet(false);
      notifyError("Seguro não possui bilhete para emitir");
    }
  }, []);

  const printTermInsurance = useCallback((row) => {
    window.open(
      `/pdf?clientId=${clientId}&contractId=${row.codigoAdesao}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, []);

  async function handleClickSendEmail(row) {
    if (handleOpenModalSendMailTermInsurance) {
      setHandleClickRow(row);
      handleOpenModalSendMailTermInsurance(true);
    }
  }

  const getData = useCallback(async () => {
    try {
      if (hasSecurity) {
        try {
          const response = await api.get("client_insurances", {
            client_id: clientId,
          });

          const insurancesFormatted = response.data.map((insurance) => {
            insurance.valorParcela = `R$ ${insurance.valorParcela}`;
            return insurance;
          });

          setInsurances(insurancesFormatted);
        } catch (insuranceErr) {
          if (isNoActiveInsurancesForHolderError(insuranceErr)) {
            setInsurances([]);
          } else {
            throw insuranceErr;
          }
        }
      } else {
        setInsurances([]);
      }

      const responseDependents = await api.get("client_dependants", {
        client_id: clientId,
        filter: "null" || "ativos",
      });

      const dependentsFormatted = responseDependents.data.map((dependent) => {
        dependent.dataNascimento = format(
          new Date(dependent.dataNascimento),
          "dd/MM/yyyy"
        );
        return dependent;
      });

      setDependents(dependentsFormatted);
    } catch (err) {
      if (
        !err?.message?.includes("Access forbidden") &&
        !err?.message?.includes("status: 404")
      ) {
        setHasErrorOnInsurancesRequest(true);
        throw err;
      }
    }
  }, [clientId, hasSecurity]);

  async function cancelInsurance(row) {
    setLoadingInsurance(true);
    try {
      await api.delete("client_insurances", {
        client_id: clientId,
        codigoAdesao: row.codigoAdesao,
        meioCancelamento: "SNXPAYMENT",
        usuarioCancelamento: user?.username,
        lojaCancelamento: 1,
        descricaoSeguro: row.descricaoSeguro,
      });
      notifySucces(`Seguro ${row.descricaoSeguro} cancelado com sucesso`);
      await getData();
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoadingInsurance(false);
    }
  }

  function openModalDetailInsurance(row) {
    setDetailInsurance(row);
    setDetailInsuranceModalOpen(true);
  }

  function closeModalDetailInsurance() {
    setDetailInsurance(null);
    setDetailInsuranceModalOpen(false);
  }

  const [columnsInsurance, setColumnsInsurance] = useState([]);

  useEffect(() => {
    setColumnsInsurance([
      {
        title: "Cód adesão",
        dataIndex: "codigoAdesao",
        key: "codigoAdesao",
      },
      {
        title: "Data de adesão",
        dataIndex: "dataAdesao",
        key: "dataAdesao",
      },
      {
        title: "Seguro",
        dataIndex: "descricaoSeguro",
        key: "descricaoSeguro",
      },
      {
        title: "Valor da parcela",
        dataIndex: "valorParcela",
        key: "valorParcela",
      },
      {
        title: "Ações",
        dataIndex: "",
        key: "",
        render: (row) => (
          <div style={{ paddingLeft: "10px" }}>
            {row.possuiBilhete &&
              (loadingPdfBilhet ? (
                <LoadingOutlined style={{ marginRight: "10px" }} />
              ) : (
                <FilePdfOutlined
                  style={{ marginRight: "10px" }}
                  title="Emitir bilhete"
                  onClick={() => printTicketInsurance(row)}
                />
              ))}
            {row.possuiTermoAdesao &&
              (loadingPdfTerm ? (
                <LoadingOutlined style={{ marginRight: "10px" }} />
              ) : (
                <FilePdfOutlined
                  style={{ marginRight: "10px" }}
                  title="Emitir termo adesão"
                  onClick={() => printTermInsurance(row)}
                />
              ))}
            {permissions && permissions.includes("cancel_insurance") && (
              <Popover
                content={
                  <Button onClick={() => cancelInsurance(row)}>
                    Confirmar cancelamento
                  </Button>
                }
                title="Deseja cancelar o seguro?"
                trigger="click"
                placement="top"
              >
                <StopOutlined
                  style={{ marginRight: "10px" }}
                  title="Cancelar"
                />
              </Popover>
            )}
            {row.possuiTermoAdesao &&
              (loadingPdfTerm ? (
                <LoadingOutlined style={{ marginRight: "10px" }} />
              ) : (
                <SendOutlined
                  style={{ marginRight: "10px" }}
                  onClick={() => handleClickSendEmail(row)}
                />
              ))}
            <InfoCircleOutlined onClick={() => openModalDetailInsurance(row)} />
          </div>
        ),
      },
    ]);
  }, [insurances, loadingPdfTerm, loadingPdfBilhet]);

  const getCanceledInsurances = useCallback(async () => {
    try {
      if (hasSecurity) {
        const { data } = await api.get("get_canceled_insurances", {
          client_id: clientId,
        });

        setCanceledInsurances(data);
      }
    } catch (error) {
      if (
        !error?.message?.includes("Access forbidden") &&
        !error?.message?.includes("status: 404")
      ) {
        setHasErrorOnInsurancesRequest(true);
        throw error;
      }
    }
  }, [clientId, hasSecurity]);

  const fetchData = async () => {
    if (!hasSecurity) {
      setInsurances([]);
      setCanceledInsurances([]);
      return;
    }
    setLoadingInsurance(true);
    setLoadingCanceledInsurances(true);
    try {
      const [requestInsuranceData, requestCanceledInsurances] =
        await Promise.allSettled([getData(), getCanceledInsurances()]);

      const hasErrorInAnyRequest =
        requestInsuranceData.status === "rejected" ||
        requestCanceledInsurances.status === "rejected";

      setHasErrorOnInsurancesRequest(hasErrorInAnyRequest);
      if (hasErrorInAnyRequest)
        notifyError("Falha ao listar seguros contratados");
    } finally {
      setLoadingInsurance(false);
      setLoadingCanceledInsurances(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, getData]);

  const { observe } = useInView({
    threshold: 0.1,
    onEnter: ({ unobserve }) => {
      if (clientId) {
        fetchData().then(unobserve);
      }
    },
  });

  return {
    refInsurance: observe,
    insurances,
    dependents,
    columnsInsurance,
    loadingInsurance,
    reload: getData,
    reloadAllInsurances: fetchData,
    detailInsuranceModalOpen,
    closeModalDetailInsurance,
    detailInsurance,
    handleClickRow,
    canceledInsurances,
    loadingCanceledInsurances,
    hasErrorOnInsurancesRequest,
    reloadCanceledInsurances: getCanceledInsurances,
    hasSecurity,
  };
}

export default useInsurance;
